import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Phone, 
  MessageCircle, 
  MapPin, 
  Clock,
  Car,
  Package,
  CheckCircle,
  Navigation,
  Radio,
  Square,
  Star,
  Pencil,
  Camera,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtime } from '@/contexts/RealtimeContext';
import LiveMap from '@/components/LiveMap';
import ChatWindow from '@/components/ChatWindow';
import EditRouteDialog from '@/components/EditRouteDialog';
import InstallBanner from '@/components/InstallBanner';
import TripStrip from '@/components/TripStrip';
import TripPhotoGallery from '@/components/TripPhotoGallery';
import { API } from '@/lib/api';
import { openMaps, getMapsHttpsFallbackUrl } from '@/lib/mapsUtils';

// Trip progress rendering was moved to <TripStrip /> (2026-07 redesign).
// The old vertical timeline lived here — see git history if you need it back.



export default function TrackingPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isDriverRoute = location.pathname.startsWith('/driver/');
  const { user, token, loading: authLoading } = useAuth();
  const { 
    driverLocation, 
    startCustomerTracking, 
    stopCustomerTracking, 
    startDriverTracking,
    stopDriverTracking,
    isTracking,
    setActiveBooking 
  } = useRealtime();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [editRouteOpen, setEditRouteOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [claiming, setClaiming] = useState(false);

  // Rating + tipping (customer side, after completion)
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [tipPreset, setTipPreset] = useState(null);     // 0 | 1 | 2 | 3 | 'custom'
  const [tipCustomDollars, setTipCustomDollars] = useState("");
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [proofPromptForBooking, setProofPromptForBooking] = useState(null);
  const submitRating = async () => {
    if (!ratingValue) { toast.error("Pick a star rating first"); return; }
    let tipAmount = 0;
    if (typeof tipPreset === 'number') tipAmount = tipPreset;
    else if (tipPreset === 'custom') tipAmount = parseFloat(tipCustomDollars) || 0;
    setRatingSubmitting(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/rate-tip`, {
        rating: ratingValue,
        comment: ratingComment.trim(),
        tip_amount: tipAmount,
      });
      toast.success(tipAmount > 0 ? `Thanks! ${ratingValue}★ + $${tipAmount.toFixed(2)} tip submitted` : `Thanks for the ${ratingValue}★!`);
      // Refresh booking so the modal hides (rating field now set)
      const r = await axios.get(`${API}/bookings/${bookingId}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      setBooking(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit rating");
    } finally {
      setRatingSubmitting(false);
    }
  };

  // Treat admin as driver-capable too — sole-operator support so admin can run
  // trips. Match by driver_id OR by role==admin (admin can supervise any trip).
  // 2026-05-28: On the /driver/* route, treat any authenticated driver as the
  // driver (lets the action bar render the moment they land, even if
  // booking.driver_id hasn't propagated yet). Polling refreshes within 5s
  // and the backend rejects unauthorized advance calls anyway, so this can't
  // cause stolen trips — and it unblocks the "I claimed but action bar
  // didn't show" case.
  const isDriver = !!user && (
    booking?.driver_id === user.id ||
    (user.role === 'admin' && (!booking?.driver_id || booking.driver_id === user.id)) ||
    (isDriverRoute && (user.role === 'driver' || user.role === 'admin'))
  );
  const canChat = true;
  // canManuallyClaim controls a VISIBLE button — admin can always tap to
  // claim the trip themselves regardless of which URL they landed on.
  //
  // 2026-07-26: REMOVED the auto-claim-on-open behaviour. Previously,
  // opening `/driver/track/{id}` (including via a push-notification tap)
  // would silently POST /driver/accept-booking. That meant tapping a
  // "new ride request" notification just to peek at the booking would
  // auto-claim it — with the owner then unable to reassign without a
  // full flow change. Claiming is now strictly opt-in via the visible
  // "Drive this" button below.
  const canManuallyClaim = booking && !booking.driver_id && booking.status !== 'completed' && booking.status !== 'cancelled' && (user?.role === 'admin' || user?.role === 'driver');

  // Driver-route gating: redirect to login if not authenticated as driver
  // (admin also allowed — sole-operator support)
  useEffect(() => {
    if (!isDriverRoute || authLoading) return;
    if (!user) {
      navigate(`/login?redirect=/driver/track/${bookingId}`);
    } else if (user.role !== 'driver' && user.role !== 'admin') {
      toast.error('Driver login required');
      navigate(`/track/${bookingId}`);
    }
  }, [isDriverRoute, user, authLoading, bookingId, navigate]);

  // Push notification reassign redirect — when the admin (or another
  // driver) taps the "New ride request" push AFTER a different driver has
  // already claimed the trip, jump them straight to the reassign dialog
  // instead of the driver view. Only fires:
  //   - on `/driver/*` route  (never on customer track view)
  //   - when the URL query includes `?from=push` (backend adds this)
  //   - when the booking has a driver_id AND the current user is NOT that driver
  //   - for admins (drivers can't reassign)
  useEffect(() => {
    if (!isDriverRoute || authLoading || !user || !booking) return;
    const fromPush = new URLSearchParams(location.search).get('from') === 'push';
    if (!fromPush) return;
    if (user.role !== 'admin') return;
    if (!booking.driver_id) return;                 // still unclaimed — normal flow
    if (booking.driver_id === user.id) return;      // admin is the driver — normal flow
    if (['completed', 'cancelled'].includes(booking.status)) return;
    toast.info('Trip already claimed — opening reassign');
    navigate(`/driver/bookings?reassign=${bookingId}`, { replace: true });
  }, [isDriverRoute, authLoading, user, booking, bookingId, location.search, navigate]);

  // Manual claim — the driver taps the "Drive this" button after
  // reviewing the booking. Never fires automatically.
  const claimBooking = useCallback(async () => {
    if (!canManuallyClaim || claiming) return;
    setClaiming(true);
    try {
      await axios.post(
        `${API}/driver/accept-booking/${bookingId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Trip claimed — GPS broadcasting');
      // Refresh booking so isDriver becomes true
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Could not claim trip';
      toast.error(msg);
    } finally {
      setClaiming(false);
    }
  }, [bookingId, token, canManuallyClaim, claiming]);

  const fetchBooking = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
    } catch (err) {
      console.error('Failed to fetch booking:', err);
      toast.error('Could not load booking');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  // Initial fetch + polling for booking status & driver_location
  useEffect(() => {
    if (!bookingId) return;
    fetchBooking();
    const interval = setInterval(fetchBooking, 5000);
    return () => clearInterval(interval);
  }, [bookingId, fetchBooking]);

  // Load driver's preferred maps app (Google/Apple/Waze) so we can auto-open
  // it when starting a new leg.
  const [mapsSettings, setMapsSettings] = useState(null);
  useEffect(() => {
    if (!isDriver) return;
    axios.get(`${API}/driver/settings`)
      .then((r) => setMapsSettings(r.data))
      .catch(() => setMapsSettings({ preferred_maps: 'google' }));
  }, [isDriver]);

  // Set active booking once available so chat polling knows what to fetch
  useEffect(() => {
    if (booking) setActiveBooking(booking);
    return () => setActiveBooking(null);
  }, [booking?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Customer-side: poll driver location
  useEffect(() => {
    if (!bookingId || isDriver) return;
    startCustomerTracking(bookingId);
    return () => stopCustomerTracking();
  }, [bookingId, isDriver, startCustomerTracking, stopCustomerTracking]);

  // Driver-side: start GPS broadcast when this is the driver
  useEffect(() => {
    if (!isDriver) return;
    startDriverTracking(bookingId);
    return () => stopDriverTracking();
  }, [isDriver, bookingId, startDriverTracking, stopDriverTracking]);

  // Resolve the destination coords + address for a given leg index.
  // 0 = pickup, 1..N = stops[i-1], N+1 = dropoff.
  const legDestination = (b, legIndex) => {
    if (!b) return null;
    const stops = Array.isArray(b.stops) ? b.stops : [];
    const lastLeg = stops.length + 1;
    if (legIndex === 0) {
      return { address: b.pickup_address, coords: b.pickup_coords };
    }
    if (legIndex === lastLeg) {
      return { address: b.dropoff_address, coords: b.dropoff_coords };
    }
    const s = stops[legIndex - 1];
    return s ? { address: s.address, coords: s.coords } : null;
  };

  const advanceTrip = async (paymentStatus) => {
    if (!token) return;
    // When the driver taps "Got Cash → Complete" or "Got JIM → Complete",
    // prompt for an optional tip. Tips are non-taxable and roll straight into
    // driver_payout. We only ask on the completion step so it doesn't nag on
    // every phase transition.
    let tipDollars = 0;
    if (paymentStatus === 'cash' || paymentStatus === 'jim_card') {
      const raw = window.prompt(
        `Did the customer leave a tip?\n\n` +
        `Enter the tip amount in dollars (or leave blank / hit cancel for $0).\n\n` +
        `Tips are non-taxable and 100% goes to the driver.`,
        ""
      );
      if (raw !== null) {
        const parsed = parseFloat(raw);
        if (!isNaN(parsed) && parsed > 0) tipDollars = parsed;
      }
    }
    // Pre-open a window SYNCHRONOUSLY inside the user gesture. iOS Safari and
    // most mobile browsers block any window.open() that fires after an
    // `await` because it's no longer trusted as user-initiated. We open
    // about:blank now and redirect it once we know the destination.
    //
    // Edge (desktop and Android) breaks catastrophically if we then set
    // `location.href` on that tab to a native URI scheme like `maps://` —
    // it pops a "This webpage had to close" dialog. So we detect Edge and
    // skip the pre-opened tab; on Edge we call openMaps() (which routes
    // straight to the HTTPS fallback URL) after the API call resolves.
    // Edge does NOT enforce the "must be user-gesture" rule the same way
    // iOS Safari does, so post-await openMaps still works.
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const isEdge = /Edg\//i.test(ua);
    let mapsWin = null;
    if (!isEdge) {
      try {
        mapsWin = window.open('about:blank', '_blank');
      } catch (_) {
        mapsWin = null;
      }
    }
    setUpdatingStatus(true);
    try {
      const body = paymentStatus ? { payment_status: paymentStatus } : {};
      if (tipDollars > 0) body.tip = tipDollars;
      const r = await axios.post(
        `${API}/bookings/${bookingId}/advance`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBooking(r.data);
      if (r.data.status === 'completed') {
        toast.success('Trip completed!');
        stopDriverTracking();
        if (mapsWin && !mapsWin.closed) { try { mapsWin.close(); } catch (_) {} }
        // Auto-attach photo prompt — offer the driver one seamless tap to
        // snap delivery / arrival proof. Delay navigate so the prompt shows.
        if (isDriver && r.data.service_type === 'package') {
          setProofPromptForBooking(r.data.id);
        } else {
          setTimeout(() => navigate('/driver/bookings', { replace: true }), 1200);
        }
      } else {
        const phase = (r.data.phase || '').replace(/_/g, ' ');
        toast.success(`Now: ${phase}`);
        // Auto-open the driver's preferred maps app when we just entered a
        // new "on the way to X" phase. Skip if no coords (avoids 0,0 issue).
        if ((r.data.phase || '').startsWith('en_route_')) {
          const dest = legDestination(r.data, r.data.leg_index ?? 0);
          if (dest && dest.address) {
            const provider = mapsSettings?.preferred_maps || 'google';
            const loc = {
              lat: dest.coords?.lat ?? null,
              lng: dest.coords?.lng ?? null,
              address: dest.address,
            };
            // Prefer redirecting the pre-opened window (survives iOS popup
            // blocker). We push the HTTPS FALLBACK URL rather than the
            // native scheme so browsers that don't recognise `maps://` /
            // `waze://` (Edge, some corporate policies, headless envs)
            // never see the "webpage had to close" dialog. On iOS Safari
            // and Android Chrome the HTTPS URL is universal-linked back
            // to the native app anyway.
            const httpsUrl = getMapsHttpsFallbackUrl(provider, loc);
            if (mapsWin && !mapsWin.closed) {
              try {
                mapsWin.location.href = httpsUrl;
              } catch (_) {
                window.open(httpsUrl, '_blank', 'noopener,noreferrer');
              }
            } else {
              // Edge path (or pre-open failed): use the iframe/anchor
              // helper which handles Edge detection internally.
              openMaps(provider, loc);
            }
          } else if (mapsWin && !mapsWin.closed) {
            try { mapsWin.close(); } catch (_) {}
          }
        } else if (mapsWin && !mapsWin.closed) {
          // Not an en_route phase — close the placeholder tab we opened.
          try { mapsWin.close(); } catch (_) {}
        }
      }
    } catch (err) {
      if (mapsWin && !mapsWin.closed) { try { mapsWin.close(); } catch (_) {} }
      toast.error(err.response?.data?.detail || 'Could not advance trip');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const markPaid = async (status) => {
    if (!token) return;
    try {
      await axios.post(
        `${API}/bookings/${bookingId}/payment?payment_status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Marked ${status}`);
      const r = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(r.data);
    } catch (e) {
      toast.error('Failed to update payment');
    }
  };

  // Driver-side: release a claimed job back into the queue WITHOUT cancelling
  // the customer's trip. The booking flips back to status=confirmed and any
  // other driver (or this driver later) can re-accept it.
  const [releasing, setReleasing] = useState(false);
  const releaseBooking = async () => {
    if (!booking) return;
    const reason = window.prompt(
      "Release this job back to the queue?\n\n" +
      "• The customer is NOT cancelled — another driver can pick it up.\n" +
      "• Optionally tell admins why (car trouble, schedule conflict, etc.):"
    );
    if (reason === null) return; // user cancelled prompt
    setReleasing(true);
    try {
      await axios.post(
        `${API}/driver/release-booking/${bookingId}${reason.trim() ? `?reason=${encodeURIComponent(reason.trim())}` : ''}`,
        {},
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      toast.success("Job released — another driver can claim it");
      stopDriverTracking();
      setTimeout(() => navigate('/driver/bookings', { replace: true }), 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not release job");
    } finally {
      setReleasing(false);
    }
  };

  // Customer-side cancellation. Allowed while pending or confirmed; backend
  // also enforces this (in_progress / completed return 400).
  const [cancelling, setCancelling] = useState(false);
  const cancelMyBooking = async () => {
    if (!booking) return;
    const ok = window.confirm(
      "Cancel this booking?\n\n" +
      "• If you paid by card, we'll issue a refund manually within 1–2 business days.\n" +
      "• If you chose Cash or JIM, nothing to refund.\n\n" +
      "Tap OK to confirm cancellation."
    );
    if (!ok) return;
    setCancelling(true);
    try {
      const r = await axios.post(`${API}/bookings/${bookingId}/cancel`);
      setBooking(r.data);
      toast.success("Booking cancelled. We'll be in touch if a refund is needed.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally {
      setCancelling(false);
    }
  };

  // Compute the current leg label + next-action label for the smart button.
  // Mirrors the backend state machine in /api/bookings/{id}/advance.
  const computeTripUi = (b) => {
    const stops = Array.isArray(b?.stops) ? b.stops : [];
    const lastLeg = stops.length + 1; // 0=pickup, 1..N=stops, N+1=dropoff
    const leg = typeof b?.leg_index === 'number' ? b.leg_index : 0;
    const phase = b?.phase || '';
    const legPretty = (i) => i === 0 ? 'Pickup' : i === lastLeg ? 'Dropoff' : `Stop ${i}`;
    const current = legPretty(leg);

    if (b?.status === 'completed') {
      return { label: 'Trip completed', disabled: true, current: 'Done' };
    }
    if (phase === '' || phase === 'driver_assigned') {
      return { label: `Start trip → On the way to ${legPretty(0)}`, current: 'Not started' };
    }
    if (phase.startsWith('en_route_')) {
      return { label: `✓ I've arrived at ${current}`, current: `Heading to ${current}` };
    }
    if (phase.startsWith('at_')) {
      if (leg >= lastLeg) {
        // At dropoff — caller will handle payment prompt before calling advance
        return { label: 'Complete trip', current: `At ${current}` };
      }
      const next = legPretty(leg + 1);
      return { label: `Depart → Heading to ${next}`, current: `At ${current}` };
    }
    return { label: 'Advance trip', current };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-5">
        <Card className="bg-[#121212] border-white/5 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <p className="text-zinc-400">Booking not found</p>
            <Button onClick={() => navigate('/')} className="mt-4 bg-[#0047FF] text-black" data-testid="go-home-button">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const ServiceIcon = booking.service_type === 'ride' ? Car : Package;
  // For driver view, derive driver location from active GPS state; for customer, from booking polling
  const mapLocation = isDriver ? driverLocation : (driverLocation || booking.driver_location);

  return (
    <div className="min-h-screen bg-[#050505] pb-44" data-testid="tracking-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-[#050505]/95 backdrop-blur border-b border-white/5">
        <div className="px-4 py-3 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(isDriver ? '/driver/bookings' : '/')}
            className="text-zinc-400 hover:text-white"
            data-testid="tracking-back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-semibold text-white flex items-center gap-2">
              <ServiceIcon className="w-4 h-4 text-[#4D88FF]" />
              {isDriver ? 'Live Trip' : `Track Your ${booking.service_type === 'ride' ? 'Ride' : 'Delivery'}`}
            </h1>
            <p className="text-xs text-zinc-500">Order #{booking.id?.slice(0, 8)}</p>
          </div>
          {isDriver && isTracking && (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30" data-testid="gps-active-badge">
              <Radio className="w-3 h-3 mr-1 animate-pulse" />
              Live
            </Badge>
          )}
        </div>
      </header>

      {/* Map */}
      <div className="relative">
        <LiveMap
          driverLocation={mapLocation}
          pickupLocation={booking.pickup_coords}
          dropoffLocation={booking.dropoff_coords}
          height="40vh"
        />

        {/* Turn-by-turn panel disabled (2026-07-31) — text was too small
            to read while driving. Drivers use the "Open" buttons on the
            pickup/dropoff rows (or the addresses themselves) to hand off
            to Apple/Google Maps, which is what actually happens on the
            road anyway. Backend `/api/geo/directions?steps=true` still
            exists in case we bring an in-app version back with better UI. */}
        
        {/* Driver/Customer info overlay — compact so it doesn't eat the map */}
        <div className="absolute bottom-2 left-2 right-2">
          {/* Contact / driver info card */}
          <Card className="bg-[#121212]/95 backdrop-blur border-white/10">
            <CardContent className="p-2.5">
              {/* Customer-facing rich driver card. Shows vehicle + plate so
                  the rider can spot the arriving car at a glance. */}
              {!isDriver && booking.driver_id ? (
                <div className="space-y-2" data-testid="meet-your-driver-card">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0047FF] to-[#003BE0] flex items-center justify-center overflow-hidden shrink-0 ring-2 ring-white/10">
                      {booking.driver_avatar_url ? (
                        <img src={booking.driver_avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white font-bold text-xl">
                          {(booking.driver_name || "?").charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-white text-base truncate" data-testid="driver-name">
                          {booking.driver_name || "Driver"}
                        </p>
                        {booking.driver_rating != null && (
                          <span className="flex items-center gap-0.5 text-[11px] text-amber-400 font-bold">
                            <Star className="w-3 h-3 fill-amber-400" />
                            {Number(booking.driver_rating).toFixed(1)}
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-bold mt-0.5">
                        Verified Link driver
                      </p>
                    </div>
                    <div className="flex gap-1.5">
                      {booking.driver_phone && (
                        <Button
                          variant="outline"
                          size="icon"
                          className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 h-9 w-9"
                          onClick={() => window.open(`tel:${booking.driver_phone}`, "_self")}
                          data-testid="call-button"
                        >
                          <Phone className="w-4 h-4" />
                        </Button>
                      )}
                      {canChat && (
                        <Button
                          variant="outline"
                          size="icon"
                          className="border-[#0047FF]/30 text-[#7AADFF] hover:bg-[#0047FF]/10 h-9 w-9"
                          onClick={() => setShowChat(true)}
                          data-testid="chat-button"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {(booking.driver_vehicle_make || booking.driver_license_plate) && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-lg bg-zinc-900/60 border border-white/5 px-3 py-2">
                        <p className="text-[9px] uppercase tracking-[0.22em] text-white/40 font-bold">Vehicle</p>
                        <p className="text-sm font-bold text-white truncate" data-testid="driver-vehicle">
                          {[
                            booking.driver_vehicle_color,
                            booking.driver_vehicle_year,
                            booking.driver_vehicle_make,
                            booking.driver_vehicle_model,
                          ].filter(Boolean).join(" ") || "—"}
                        </p>
                      </div>
                      <div className="rounded-lg bg-[#0047FF]/10 border border-[#0047FF]/30 px-3 py-2">
                        <p className="text-[9px] uppercase tracking-[0.22em] text-[#4D88FF] font-bold">Plate</p>
                        <p className="text-sm font-black text-white tracking-widest font-mono" data-testid="driver-plate">
                          {(booking.driver_license_plate || "—").toUpperCase()}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20 px-3 py-2">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    <p className="text-[11px] text-emerald-200/90 leading-tight">
                      Background check on file · Insurance verified · Live GPS active
                    </p>
                  </div>
                </div>
              ) : (
                /* Driver-facing (their customer's info) — compact. */
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-[#0047FF]/20 flex items-center justify-center shrink-0">
                    {isDriver ? (
                      <span className="text-[#4D88FF] font-bold text-sm">
                        {booking.customer_name?.charAt(0) || "?"}
                      </span>
                    ) : (
                      <Car className="w-4 h-4 text-[#4D88FF]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate leading-tight">
                      {isDriver ? booking.customer_name : "Awaiting driver"}
                    </p>
                    <p className="text-[11px] text-zinc-400 truncate">
                      {isDriver ? booking.customer_phone : "Will assign shortly"}
                    </p>
                  </div>
                  {canManuallyClaim && (
                    <Button
                      onClick={claimBooking}
                      disabled={claiming}
                      className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-xs px-3 py-1.5 h-auto rounded-lg"
                      data-testid="manual-claim-button"
                      title="Drive this trip yourself — claims it as the driver"
                    >
                      <Car className="w-3.5 h-3.5 mr-1" />
                      {claiming ? "Claiming…" : "Drive this"}
                    </Button>
                  )}
                  <div className="flex gap-1.5 shrink-0">
                    {(isDriver ? booking.customer_phone : booking.driver_phone) && (
                      <Button
                        variant="outline"
                        size="icon"
                        className="border-white/10 text-emerald-400 h-8 w-8"
                        onClick={() => window.open(`tel:${isDriver ? booking.customer_phone : booking.driver_phone}`, "_self")}
                        data-testid="call-button"
                      >
                        <Phone className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {canChat && (
                      <Button
                        variant="outline"
                        size="icon"
                        className="border-white/10 text-[#4D88FF] h-8 w-8"
                        onClick={() => setShowChat(true)}
                        data-testid="chat-button"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Status Progress — horizontal trip strip (2026-07 redesign).
          Replaces the prior vertical top-to-bottom timeline with a modern
          horizontally-scrollable card row + "next up" callout. */}
      <div className="px-4 py-6">
        {/* First-time customer install nudge — auto-hides for installed PWAs */}
        {!isDriver && <div className="mb-4"><InstallBanner testIdPrefix="track-install-banner" /></div>}

        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-zinc-400">Trip progress</h3>
        </div>
        <TripStrip booking={booking} />
        {booking?.status === 'completed' && (
          <div className="mt-4">
            <TripPhotoGallery bookingId={booking.id} />
          </div>
        )}
      </div>

      {/* Booking Details */}
      <div className="px-4 pb-6">
        <Card className="bg-[#121212] border-white/5">
          <CardContent className="p-4 space-y-4">
            {/* Edit-route button — visible to the customer who booked this
                trip AND admins. Hidden once the trip is completed/cancelled.
                Backend enforces authorization; this is just the UI gate. */}
            {(() => {
              const canEditRoute =
                booking.status !== "completed" &&
                booking.status !== "cancelled" &&
                booking.status !== "no_show" &&
                (booking.user_id === user?.id || user?.role === "admin" || user?.is_admin === true);
              if (!canEditRoute) return null;
              return (
                <div className="flex items-center justify-between rounded-md bg-[#0047FF]/5 border border-[#0047FF]/20 px-3 py-2">
                  <div className="text-xs">
                    <p className="text-white/80 font-bold">Change pickup, drop-off, or stops</p>
                    <p className="text-white/40 text-[10px]">
                      {booking.status === "in_progress"
                        ? "Trip is live — driver will be notified instantly."
                        : "Update the route any time before the driver arrives."}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditRouteOpen(true)}
                    className="h-7 px-2 text-[11px] border-[#0047FF]/40 text-[#7AADFF] hover:bg-[#0047FF]/10 shrink-0"
                    data-testid="tracking-edit-route"
                  >
                    <Pencil className="w-3 h-3 mr-1" /> Edit route
                  </Button>
                </div>
              );
            })()}
            {/* Per-leg "Open in Maps" — tappable rows + explicit Open
                buttons. Visible to the assigned driver, admins (owner-
                supervision), and the customer themselves (so if they
                accidentally close the map, one tap on the address re-
                opens turn-by-turn navigation to that stop). */}
            {(() => {
              const isAssignedDriver = booking.driver_id === user?.id;
              const isBookingOwner = booking.user_id === user?.id;
              const isAdminUser = user?.role === 'admin' || user?.is_admin === true;
              const finished = ['completed', 'cancelled'].includes(booking.status);
              const showMapsBtns = (isAssignedDriver || isBookingOwner || isAdminUser) && !finished;
              const provider = mapsSettings?.preferred_maps || 'google';
              const openLeg = (loc) => openMaps(provider, {
                lat: loc?.coords?.lat ?? null,
                lng: loc?.coords?.lng ?? null,
                address: loc?.address || '',
              });
              return (
                <>
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-emerald-500/10">
                      <MapPin className="w-4 h-4 text-emerald-400" />
                    </div>
                    {showMapsBtns ? (
                      <button
                        type="button"
                        onClick={() => openLeg({ address: booking.pickup_address, coords: booking.pickup_coords })}
                        className="min-w-0 flex-1 text-left group"
                        data-testid="tap-pickup-address"
                        title="Tap to open in maps"
                      >
                        <p className="text-xs text-zinc-500 group-hover:text-emerald-300">Pickup · tap to navigate</p>
                        <p className="text-sm text-white underline decoration-emerald-500/40 underline-offset-2 group-hover:decoration-emerald-400">
                          {booking.pickup_address}
                        </p>
                      </button>
                    ) : (
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-zinc-500">Pickup</p>
                        <p className="text-sm text-white">{booking.pickup_address}</p>
                      </div>
                    )}
                    {showMapsBtns && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => openLeg({ address: booking.pickup_address, coords: booking.pickup_coords })}
                        className="shrink-0 h-8 px-2 text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10"
                        data-testid="open-maps-pickup"
                      >
                        <Navigation className="w-3.5 h-3.5 mr-1" />Open
                      </Button>
                    )}
                  </div>
                  {Array.isArray(booking.stops) && booking.stops.length > 0 && booking.stops.map((stop, idx) => (
                    <div key={idx} className="flex items-start gap-3" data-testid={`tracking-stop-${idx}`}>
                      <div className="p-2 rounded-lg bg-amber-500/10">
                        <MapPin className="w-4 h-4 text-amber-400" />
                      </div>
                      {showMapsBtns ? (
                        <button
                          type="button"
                          onClick={() => openLeg(stop)}
                          className="min-w-0 flex-1 text-left group"
                          data-testid={`tap-stop-address-${idx}`}
                          title="Tap to open in maps"
                        >
                          <p className="text-xs text-amber-400 font-semibold group-hover:text-amber-300">Stop {idx + 1} · tap to navigate</p>
                          <p className="text-sm text-white underline decoration-amber-500/40 underline-offset-2 group-hover:decoration-amber-400">
                            {stop.address}
                          </p>
                        </button>
                      ) : (
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-amber-400 font-semibold">Stop {idx + 1}</p>
                          <p className="text-sm text-white">{stop.address}</p>
                        </div>
                      )}
                      {showMapsBtns && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => openLeg(stop)}
                          className="shrink-0 h-8 px-2 text-amber-300 hover:text-amber-200 hover:bg-amber-500/10"
                          data-testid={`open-maps-stop-${idx}`}
                        >
                          <Navigation className="w-3.5 h-3.5 mr-1" />Open
                        </Button>
                      )}
                    </div>
                  ))}
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-[#0047FF]/10">
                      <MapPin className="w-4 h-4 text-[#4D88FF]" />
                    </div>
                    {showMapsBtns ? (
                      <button
                        type="button"
                        onClick={() => openLeg({ address: booking.dropoff_address, coords: booking.dropoff_coords })}
                        className="min-w-0 flex-1 text-left group"
                        data-testid="tap-dropoff-address"
                        title="Tap to open in maps"
                      >
                        <p className="text-xs text-zinc-500 group-hover:text-[#80B0FF]">Dropoff · tap to navigate</p>
                        <p className="text-sm text-white underline decoration-[#0047FF]/40 underline-offset-2 group-hover:decoration-[#80B0FF]">
                          {booking.dropoff_address}
                        </p>
                      </button>
                    ) : (
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-zinc-500">Dropoff</p>
                        <p className="text-sm text-white">{booking.dropoff_address}</p>
                      </div>
                    )}
                    {showMapsBtns && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => openLeg({ address: booking.dropoff_address, coords: booking.dropoff_coords })}
                        className="shrink-0 h-8 px-2 text-[#80B0FF] hover:text-white hover:bg-[#0047FF]/10"
                        data-testid="open-maps-dropoff"
                      >
                        <Navigation className="w-3.5 h-3.5 mr-1" />Open
                      </Button>
                    )}
                  </div>
                </>
              );
            })()}
            {/* Charge-customer row: admins/owners only. Drivers must
                NEVER see the customer's fare — over time they'd reverse-
                engineer the platform cut. Since customers now pay only by
                Stripe, drivers don't need this number to collect anyway. */}
            {isDriver && (user?.role === 'admin' || user?.is_admin === true) && (
              <>
                <div className="pt-3 border-t border-white/5 flex justify-between">
                  <span className="text-zinc-400">Charge customer</span>
                  <span className="font-bold text-amber-300" data-testid="driver-view-customer-total">
                    ${Number(booking.estimated_price ?? booking.total_amount ?? booking.subtotal ?? 0).toFixed(2)}
                  </span>
                </div>
                <p className="text-[10px] text-zinc-500 text-right -mt-1">
                  Admin view. Hidden from regular drivers.
                </p>
              </>
            )}
            <div className={`${isDriver && (user?.role === 'admin' || user?.is_admin === true) ? 'pt-2' : 'pt-3 border-t border-white/5'} flex justify-between`}>
              <span className="text-zinc-400">{isDriver ? 'Your payout' : 'Total'}</span>
              <span className="font-bold text-[#4D88FF]" data-testid={isDriver ? 'driver-payout-total' : 'customer-total'}>
                ${(() => {
                  if (isDriver) {
                    const locked = booking.driver_payout;
                    if (locked != null) return Number(locked).toFixed(2);
                    const sub = Number(booking.subtotal ?? booking.estimated_price ?? 0);
                    const tip = Number(booking.tip ?? 0);
                    return (sub * 0.8 + tip).toFixed(2);
                  }
                  return Number(booking.estimated_price ?? 0).toFixed(2);
                })()}
              </span>
            </div>
            {isDriver && (
              <p className="text-[10px] text-zinc-500 text-right -mt-1" data-testid="driver-payout-hint">
                After 20% platform fee{booking.tip ? ' · includes tip' : ''}.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Driver Action Bar — smart single-button state machine.
          Requires the booking to actually have been claimed (driver_id set).
          Otherwise the "Start trip → On the way to Pickup" button would
          render on unclaimed bookings and let an admin who just opened the
          notification silently advance a trip nobody's driving yet. */}
      {isDriver && booking.driver_id && booking.status !== 'completed' && (() => {
        const tripUi = computeTripUi(booking);
        const stopsCount = Array.isArray(booking.stops) ? booking.stops.length : 0;
        const lastLeg = stopsCount + 1;
        const leg = typeof booking.leg_index === 'number' ? booking.leg_index : 0;
        const atDropoff = (booking.phase || '').startsWith('at_') && leg >= lastLeg;
        const paymentMethod = booking.payment_method || 'stripe';
        const paymentPaid = ['paid', 'cash', 'jim_card', 'stripe_tap'].includes(booking.payment_status);
        const isAdminViewer = user?.role === 'admin' || user?.is_admin === true;
        // Collect prompt rules:
        // - Regular driver: never (customers pay by card only).
        // - Admin at dropoff: ALWAYS show the 3-button chooser so they
        //   can record the actual payment method (Stripe/Cash/JIM) for
        //   analytics + tax reports. Works whether the customer already
        //   paid Stripe or paid offline.
        const needsCollection = atDropoff && (isAdminViewer || !paymentPaid);
        const customerTotal = Number(booking.estimated_price ?? booking.total_amount ?? booking.subtotal ?? 0);
        const driverTake = (() => {
          const locked = booking.driver_payout;
          if (locked != null) return Number(locked);
          const sub = Number(booking.subtotal ?? booking.estimated_price ?? 0);
          const tip = Number(booking.tip ?? 0);
          return sub * 0.8 + tip;
        })();

        return (
          <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#050505]/95 backdrop-blur border-t border-white/5 p-4 space-y-2" style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 1rem)" }} data-testid="driver-action-bar">
            {/* Current phase + payment summary. Shows both amounts so the
                driver knows exactly what to collect from the customer AND
                what their own payout will be — the previous version hid
                the customer total and drivers ended up charging Cash/JIM
                riders the wrong amount. */}
            <div className="flex items-center justify-between gap-2 px-1" data-testid="driver-payment-row">
              <div className="text-xs">
                <span className="text-white/40 mr-1">Now:</span>
                <span className="font-bold text-[#4D88FF]" data-testid="trip-current-phase">{tripUi.current}</span>
              </div>
              <div className="text-xs">
                <span className={`font-bold ${paymentPaid ? 'text-[#00E676]' : 'text-amber-400'}`}>
                  {booking.payment_status === 'paid' ? 'PAID (Stripe)'
                    : booking.payment_status === 'cash' ? 'PAID (Cash)'
                    : booking.payment_status === 'stripe_tap' ? 'PAID (Stripe Tap)'
                    : booking.payment_status === 'jim_card' ? 'PAID (Stripe Tap)'
                    : (booking.payment_status || 'unpaid').toUpperCase()}
                </span>
              </div>
            </div>
            {/* Amount readout: for regular drivers this is ONLY their
                payout. Admins see both fare + payout so they can still
                run offline settlements when needed. */}
            <div className={`grid gap-2 px-1 ${isAdminViewer ? 'grid-cols-2' : 'grid-cols-1'}`} data-testid="driver-amount-readout">
              {isAdminViewer && (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/25 px-2 py-1.5">
                  <p className="text-[9px] uppercase tracking-[0.18em] text-amber-300 font-bold">Charge customer</p>
                  <p className="text-sm font-black text-white" data-testid="driver-charge-customer">
                    ${customerTotal.toFixed(2)}
                  </p>
                </div>
              )}
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/25 px-2 py-1.5">
                <p className="text-[9px] uppercase tracking-[0.18em] text-emerald-300 font-bold">Your payout</p>
                <p className="text-sm font-black text-white" data-testid="driver-action-bar-payout">
                  ${driverTake.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Payment collection at dropoff.
                - For ADMINS: always show three "Complete as …" buttons so
                  they can record the actual payment method received
                  (analytics stays accurate). Works whether the customer
                  paid Stripe upfront or paid Cash/JIM in person.
                - For regular drivers: no collect UI (customers pay by card
                  only), just the generic advance/complete button. */}
            {needsCollection ? (
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="collect-payment-prompt">
                <p className="text-xs font-bold text-amber-200 uppercase tracking-wider">
                  {paymentPaid
                    ? `Confirm how the customer actually paid ($${customerTotal.toFixed(2)})`
                    : `Collect $${customerTotal.toFixed(2)} from customer`}
                </p>
                <p className="text-[10px] text-amber-100/70 -mt-1">
                  {paymentPaid
                    ? "Records the actual method for your tax + analytics reports."
                    : "That's what the customer owes on their receipt. Your payout is settled separately."}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    onClick={() => advanceTrip('paid')}
                    disabled={updatingStatus}
                    className="bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold text-xs"
                    data-testid="collect-stripe-button"
                  >
                    Stripe
                  </Button>
                  <Button
                    onClick={() => advanceTrip('stripe_tap')}
                    disabled={updatingStatus}
                    className="bg-[#4D88FF] hover:bg-[#3070EE] text-white font-bold text-xs"
                    data-testid="collect-stripe-tap-button"
                  >
                    Stripe Tap
                  </Button>
                  <Button
                    onClick={() => advanceTrip('cash')}
                    disabled={updatingStatus}
                    className="bg-[#00E676] hover:bg-[#00C766] text-black font-bold text-xs"
                    data-testid="collect-cash-button"
                  >
                    Cash
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                onClick={() => advanceTrip()}
                disabled={updatingStatus || tripUi.disabled}
                className="w-full h-12 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0"
                data-testid="advance-trip-button"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {tripUi.label}
              </Button>
            )}

            {/* GPS broadcast toggle */}
            {isTracking ? (
              <Button onClick={stopDriverTracking} variant="ghost" size="sm" className="w-full text-zinc-500" data-testid="stop-gps-button">
                <Square className="w-3 h-3 mr-1" /> Pause GPS broadcast
              </Button>
            ) : (
              <Button onClick={() => startDriverTracking(bookingId)} variant="ghost" size="sm" className="w-full text-[#4D88FF]" data-testid="start-gps-button">
                <Radio className="w-3 h-3 mr-1" /> Resume GPS broadcast
              </Button>
            )}

            {/* Release back to queue — driver only, hidden once trip completed */}
            {booking.driver_id === user?.id && (
              <Button
                onClick={releaseBooking}
                disabled={releasing}
                variant="ghost"
                size="sm"
                className="w-full text-red-400/80 hover:text-red-300 hover:bg-red-500/10"
                data-testid="release-booking-button"
              >
                {releasing ? "Releasing…" : "↩ Release job back to queue"}
              </Button>
            )}
          </div>
        );
      })()}

      {/* Unclaimed-booking CTA — replaces the driver action bar on trips
          nobody is driving yet. This is what admins/drivers see when they
          land here from a "New ride request" push notification. Explicit
          tap to claim; no silent auto-accept. */}
      {isDriverRoute && canManuallyClaim && (() => {
        // Auto-route preview: sizing up the trip BEFORE claiming. Payout
        // uses the standard 80% driver share (server re-computes on real
        // claim using live commission config). Drive time is a fast
        // client-side estimate — 25 mph average for local rides, floored
        // at 5 minutes so short pickups don't look absurd.
        const miles = Number(booking.distance_miles) || 0;
        const price = Number(booking.subtotal ?? booking.estimated_price) || 0;
        const payoutEst = price * 0.8;
        const durationEst = booking.estimated_duration_minutes
          || booking.duration_minutes
          || (miles > 0 ? Math.max(5, Math.round((miles / 25) * 60)) : 0);
        return (
          <div
            className="fixed bottom-0 left-0 right-0 z-40 bg-[#050505]/95 backdrop-blur border-t border-white/5 p-4 space-y-2"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 1rem)" }}
            data-testid="unclaimed-cta-bar"
          >
            {/* Route preview strip — payout / miles / drive time at a
                glance so the driver can size up the trip before tapping. */}
            <div className="grid grid-cols-3 gap-2" data-testid="route-preview">
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/25 px-2 py-1.5 text-center">
                <p className="text-[9px] uppercase tracking-[0.18em] text-emerald-300 font-bold">Est. payout</p>
                <p className="text-sm font-black text-white" data-testid="route-preview-payout">
                  ${payoutEst.toFixed(2)}
                </p>
              </div>
              <div className="rounded-lg bg-white/5 border border-white/10 px-2 py-1.5 text-center">
                <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-400 font-bold">Miles</p>
                <p className="text-sm font-black text-white" data-testid="route-preview-miles">
                  {miles > 0 ? miles.toFixed(1) : "—"}
                </p>
              </div>
              <div className="rounded-lg bg-white/5 border border-white/10 px-2 py-1.5 text-center">
                <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-400 font-bold">Drive time</p>
                <p className="text-sm font-black text-white" data-testid="route-preview-time">
                  {durationEst > 0 ? `~${durationEst} min` : "—"}
                </p>
              </div>
            </div>
            <p className="text-[11px] text-zinc-400 text-center">
              This trip is <b>not claimed</b>. Tap below to drive it — nothing happens automatically.
            </p>
            <Button
              onClick={claimBooking}
              disabled={claiming}
              className="w-full h-12 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,230,118,0.6)] border-0"
              data-testid="unclaimed-cta-claim-button"
            >
              <Car className="w-4 h-4 mr-2" />
              {claiming ? "Claiming…" : "Drive this trip"}
            </Button>
          </div>
        );
      })()}

      {/* Chat Window */}
      {showChat && canChat && (
        <ChatWindow
          bookingId={booking.id}
          otherPartyName={isDriver ? booking.customer_name : (booking.driver_name || 'Driver')}
          otherPartyPhone={isDriver ? booking.customer_phone : booking.driver_phone}
          onClose={() => setShowChat(false)}
        />
      )}

      {/* Post-trip Rating + Tip modal — only for customer view, only when trip
          is completed and not yet rated. */}
      {/* Customer-side cancel — only on the customer track view (not driver
          view), only while the trip is still pending or confirmed. Hidden
          once driver is en route / arrived / completed. */}
      {!isDriverRoute && !isDriver && booking && ["pending", "confirmed"].includes(booking.status) && (
        <div className="px-4 mt-4 mb-2" data-testid="customer-cancel-section">
          <Button
            type="button"
            variant="outline"
            onClick={cancelMyBooking}
            disabled={cancelling}
            className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
            data-testid="customer-cancel-button"
          >
            {cancelling ? "Cancelling…" : "Cancel this booking"}
          </Button>
          <p className="text-[11px] text-zinc-500 mt-2 text-center">
            Cancel anytime before the driver heads out. We'll handle any card refund manually within 1–2 business days.
          </p>
        </div>
      )}

      {!isDriverRoute && !isDriver && booking?.status === 'completed' && booking?.rating == null && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="rating-modal">
          <div className="w-full sm:max-w-md bg-[#0f0f0f] border-t sm:border border-white/10 sm:rounded-2xl p-5 space-y-4">
            <div className="text-center">
              <div className="text-3xl mb-1">🎉</div>
              <h3 className="text-lg font-bold text-white">How was your trip?</h3>
              <p className="text-xs text-zinc-400 mt-0.5">{booking.driver_name || 'Your driver'} · ${Number(booking.estimated_price || 0).toFixed(2)}</p>
            </div>

            {/* Star rating */}
            <div className="flex justify-center gap-1.5" data-testid="rating-stars">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRatingValue(n)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`rating-star-${n}`}
                >
                  <Star className={`w-9 h-9 ${n <= ratingValue ? 'fill-amber-400 text-amber-400' : 'text-zinc-600'}`} strokeWidth={1.5} />
                </button>
              ))}
            </div>

            {/* Tip presets */}
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Add a tip (optional)</p>
              <div className="grid grid-cols-5 gap-1.5" data-testid="tip-presets">
                {[0, 1, 2, 3].map(amt => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setTipPreset(amt)}
                    className={`py-2 rounded-lg text-sm font-bold border transition-colors ${tipPreset === amt ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300' : 'bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10'}`}
                    data-testid={`tip-preset-${amt}`}
                  >
                    {amt === 0 ? 'None' : `$${amt}`}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setTipPreset('custom')}
                  className={`py-2 rounded-lg text-sm font-bold border transition-colors ${tipPreset === 'custom' ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300' : 'bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10'}`}
                  data-testid="tip-preset-custom"
                >
                  Other
                </button>
              </div>
              {tipPreset === 'custom' && (
                <input
                  type="number"
                  min="0"
                  step="0.50"
                  value={tipCustomDollars}
                  onChange={(e) => setTipCustomDollars(e.target.value)}
                  placeholder="Custom tip amount ($)"
                  className="w-full px-3 py-2 text-sm bg-black/40 border border-white/10 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:border-white/30"
                  data-testid="tip-custom-input"
                  autoFocus
                />
              )}
            </div>

            {/* Optional comment */}
            <textarea
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value)}
              maxLength={500}
              placeholder="Anything you'd like the driver to know? (optional)"
              rows={2}
              className="w-full px-3 py-2 text-sm bg-black/40 border border-white/10 rounded-lg text-white placeholder:text-zinc-500 resize-none focus:outline-none focus:border-white/30"
              data-testid="rating-comment-input"
            />

            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  // Mark as skipped by setting rating to 0 won't help — backend would reject.
                  // Instead, just close the modal locally by setting booking.rating to 0 in state.
                  setBooking(prev => prev ? { ...prev, rating: -1 } : prev);  // -1 = skipped locally
                }}
                disabled={ratingSubmitting}
                className="flex-1 text-zinc-400 hover:text-zinc-200"
                data-testid="rating-skip-button"
              >
                Skip
              </Button>
              <Button
                type="button"
                onClick={submitRating}
                disabled={ratingSubmitting || !ratingValue}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold"
                data-testid="rating-submit-button"
              >
                {ratingSubmitting ? "Sending…" : "Submit"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <EditRouteDialog
        open={editRouteOpen}
        booking={booking}
        onClose={() => setEditRouteOpen(false)}
        onSaved={(updated) => setBooking(updated)}
      />

      {/* Note: the prior sticky floating action bar was removed 2026-02
          because the primary Driver Action Bar above is already fixed at
          the bottom of the viewport. Rendering both stacked them and
          caused a duplicated status/advance-button UI at the bottom of
          the live-feed. See git history for the old block. */}

      {proofPromptForBooking && (
        <ProofPhotoPrompt
          bookingId={proofPromptForBooking}
          token={token}
          onDone={() => {
            setProofPromptForBooking(null);
            setTimeout(() => navigate('/driver/bookings', { replace: true }), 400);
          }}
        />
      )}
    </div>
  );
}

// ProofPhotoPrompt — one-tap modal shown to drivers right after they mark a
// package booking as delivered. Encourages (but doesn't force) attaching a
// delivery-proof photo. Uploads via /messages/upload-photo then fires a chat
// message so both the customer (in the receipt gallery) + driver see it.
function ProofPhotoPrompt({ bookingId, token, onDone }) {
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  const handleFile = async (evt) => {
    const file = evt.target.files?.[0];
    evt.target.value = "";
    if (!file || uploading) return;
    if (!file.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('booking_id', bookingId);
      fd.append('file', file);
      const uploadRes = await fetch(`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/messages/upload-photo`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!uploadRes.ok) throw new Error(`upload failed (${uploadRes.status})`);
      const body = await uploadRes.json();
      if (body.photo_url) {
        await axios.post(
          `${API}/messages`,
          { booking_id: bookingId, text: '📷 Delivery proof', photo_url: body.photo_url },
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
      }
      setDone(true);
      toast.success('Proof photo attached to receipt.');
      setTimeout(() => onDone && onDone(), 900);
    } catch (err) {
      toast.error('Photo upload failed — you can still send one from chat.');
      onDone && onDone();
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="proof-photo-prompt"
    >
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl max-w-sm w-full p-6 text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-[#0047FF]/20 flex items-center justify-center mx-auto">
          {done ? (
            <CheckCircle className="w-7 h-7 text-emerald-400" />
          ) : (
            <Camera className="w-7 h-7 text-[#7AADFF]" />
          )}
        </div>
        <h3 className="text-lg font-semibold text-white">
          {done ? 'Photo attached' : 'Snap a proof photo?'}
        </h3>
        <p className="text-sm text-zinc-400">
          {done
            ? "The customer will see it on their receipt."
            : "Attach a quick photo showing where you dropped the package. It'll live on the customer's receipt as a keepsake record."}
        </p>
        {!done && (
          <>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFile}
              className="hidden"
              id={`proof-file-${bookingId}`}
              data-testid="proof-photo-input"
            />
            <label
              htmlFor={`proof-file-${bookingId}`}
              className={`inline-flex items-center justify-center gap-2 w-full h-11 rounded-md text-sm font-semibold cursor-pointer transition-colors ${
                uploading
                  ? 'bg-[#0047FF]/50 text-white/70'
                  : 'bg-[#0047FF] hover:bg-[#0047FF]/90 text-white'
              }`}
              data-testid="proof-photo-attach-btn"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
              ) : (
                <><Camera className="w-4 h-4" /> Attach photo</>
              )}
            </label>
            <button
              type="button"
              onClick={() => onDone && onDone()}
              className="text-xs text-zinc-400 hover:text-white underline-offset-2 hover:underline"
              data-testid="proof-photo-skip-btn"
            >
              Skip
            </button>
          </>
        )}
      </div>
    </div>
  );
}

